import { queryOptions, useQuery } from '@tanstack/react-query';

import { SECONDS_IN_HOUR } from '@cast/constants';
import { DurationString } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_AGGREGATED_WORKLOAD_CUSTOM_METRICS_QUERY_KEY =
  'woop-aggregated-workload-custom-metrics';

type Args = {
  clusterId: string;
  workloadId: string;
  containerName: string;
  fromTime: string;
  toTime: string;
  step?: DurationString;
  backfillDataPoints?: boolean;
  enabled?: boolean;
};

const getAggregatedWorkloadCustomMetricsQueryKey = (
  params: {
    clusterId?: string;
    workloadId?: string;
    containerName?: string;
    fromTime?: string;
    toTime?: string;
    step?: DurationString;
  } = {}
) =>
  [
    WOOP_AGGREGATED_WORKLOAD_CUSTOM_METRICS_QUERY_KEY,
    params.clusterId,
    params.workloadId,
    params.containerName,
    params.fromTime,
    params.toTime,
    params.step,
  ] as const;

const getAggregatedWorkloadCustomMetricsQueryOptions = ({
  clusterId,
  workloadId,
  containerName,
  fromTime,
  toTime,
  step = `${SECONDS_IN_HOUR}s`,
  backfillDataPoints,
  enabled,
}: Args) =>
  queryOptions({
    queryKey: getAggregatedWorkloadCustomMetricsQueryKey({
      clusterId,
      workloadId,
      containerName,
      fromTime,
      toTime,
      step,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.workloadOptimization.getAggregatedWorkloadCustomMetrics(
          {
            clusterId,
            workloadId,
            containerName,
            fromTime,
            toTime,
            step,
            aggregationSlidingWindow: step,
            aggregationType: 'AVERAGE',
            backfillDataPoints,
          }
        );
      return data;
    },
    enabled: makeIsEnabled(enabled, !!clusterId, !!workloadId, !!containerName),
  });

export const useAggregatedWorkloadCustomMetricsQuery = (args: Args) => {
  const { isLoading, data, error, refetch } = useQuery(
    getAggregatedWorkloadCustomMetricsQueryOptions(args)
  );

  return { isLoading, data, error, refetch };
};
